import type { AuthState } from '@classroom-ai/ui/auth/types';

import { onAuthStateChanged } from 'firebase/auth';
import { useSetState } from 'minimal-shared/hooks';
import { auth } from '@classroom-ai/firebase-config';
import { useMemo, useEffect, useCallback } from 'react';
import { AuthContext } from '@classroom-ai/ui/auth/context/auth-context';
import { UserRole, upsertUser, touchUserLogin } from '@dataconnect/generated';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

/**
 * Teacher app auth provider. Reads the role custom claim from the JWT so
 * the TeacherGuard can gate the dashboard. Every sign-in touches the SQL
 * User row's lastLogin; the row is created (with the claim's role) on the
 * first sign-in only — the login path never rewrites displayName or role
 * after that (epic #595; assignRole owns the role).
 */
export function AuthProvider({ children }: Props) {
  const { state, setState } = useSetState<AuthState>({ user: null, loading: true });

  const checkUserSession = useCallback(async () => {
    try {
      onAuthStateChanged(auth, async (user) => {
        if (user && user.emailVerified) {
          // Role claim is set by the user-provisioning Cloud Function.
          // Defaults to STUDENT for users with no claim (standard sign-up flow).
          const tokenResult = await user.getIdTokenResult();
          const role = (tokenResult.claims.role as UserRole) ?? UserRole.STUDENT;

          const touched = await touchUserLogin({ email: user.email ?? '' });
          if (!touched.data.user_update) {
            await upsertUser({
              email: user.email ?? '',
              displayName: user.displayName ?? undefined,
              role,
            });
          }

          setState({ user: Object.assign(user, { role }), loading: false });
        } else {
          setState({ user: null, loading: false });
        }
      });
    } catch (error) {
      console.error(error);
      setState({ user: null, loading: false });
    }
  }, [setState]);

  useEffect(() => {
    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user
        ? {
            ...state.user,
            id: state.user.uid,
            displayName: state.user.displayName,
            photoURL: state.user.photoURL,
            role: state.user.role,
          }
        : null,
      checkUserSession,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
    }),
    [checkUserSession, state.user, status]
  );

  return <AuthContext value={memoizedValue}>{children}</AuthContext>;
}
